import { ReactNode, useEffect } from 'react';
import { Alert, Icon, Stack, Text, useTimer } from '@hs-baumappe/legacy-ui';
import { useTranslation } from 'react-i18next';

import i18n from '../../../../i18n';
import useOvertimeDetailAlert from './hooks/useOvertimeDetailAlert';
import { OvertimeDetailAlert } from '../../OvertimeDetail.utils';

interface OvertimeDetailAlertsProps {
  name: string;
  signedManually: boolean;
}

function getAlertColor(alert: OvertimeDetailAlert) {
  switch (alert) {
    case OvertimeDetailAlert.ASSIGNED:
      return 'warning' as const;
    default:
      return 'success' as const;
  }
}

function getAlertIconName(alert: OvertimeDetailAlert) {
  switch (alert) {
    case OvertimeDetailAlert.ASSIGNED:
      return 'warning' as const;
    default:
      return 'check' as const;
  }
}

function getAlertContent(alert: OvertimeDetailAlert, name: string): ReactNode {
  switch (alert) {
    case OvertimeDetailAlert.SEND:
      return i18n.t('overtimeDetail.sendEmail.alert');
    case OvertimeDetailAlert.ARCHIVED:
      return i18n.t('overtimeDetail.archive.alert', { name });
    case OvertimeDetailAlert.SIGNED_MANUALLY:
      return i18n.t('overtimeDetail.manuallySignedPdf.success');
    case OvertimeDetailAlert.ASSIGNED:
      return (
        <Stack gap="small-5x">
          <Text component="div" variant="label-large">
            {i18n.t('document.assignedAlert.title')}
          </Text>
          <Text component="div" variant="body-small">
            {i18n.t('document.assignedAlert.message')}
          </Text>
        </Stack>
      );
    default:
      return null;
  }
}

export default function OvertimeDetailAlerts({
  name,
  signedManually,
}: OvertimeDetailAlertsProps): JSX.Element | null {
  const { t } = useTranslation();

  const { alert, clearAlert } = useOvertimeDetailAlert();
  const { setTimeoutTimer, clearAllTimers } = useTimer();

  useEffect(() => {
    if (!alert) {
      return;
    }

    setTimeoutTimer(clearAlert, 5000);

    return () => clearAllTimers();
  }, [alert, clearAlert, clearAllTimers, setTimeoutTimer]);

  if (!alert && !signedManually) {
    return null;
  }

  return (
    <Stack gap="medium">
      {alert && (
        <Alert
          className="qa-alert-message"
          color={getAlertColor(alert)}
          endSection={<Icon color="inherit" name={getAlertIconName(alert)} />}
        >
          {getAlertContent(alert, name)}
        </Alert>
      )}

      {signedManually && (
        <Alert color="warning" className="qa-alert-message">
          {t('overtimeDetail.manuallySignedPdf.signed')}
        </Alert>
      )}
    </Stack>
  );
}
